import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, addDays } from 'date-fns';
import Layout from '../components/Layout';
import { CalendarClock, ChevronLeft, ChevronRight, CheckCircle2, Clock, AlertTriangle, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (d, pattern = 'EEE, dd MMM yyyy') => {
  try { return format(parseISO(d), pattern); } catch { return d || '—'; }
};

const ScheduledCalls = ({ onLogout }) => {
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [tab, setTab] = useState('day');
  const [date, setDate] = useState('');
  const [today, setToday] = useState('');
  const [rep, setRep] = useState('');
  const [reps, setReps] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (rep) params.rep = rep;
      if (tab === 'day' && date) params.date = date;
      const url = tab === 'day' ? `${API}/scheduled-calls` : `${API}/scheduled-calls/overdue`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, params });
      setItems(res.data.items || []);
      setToday(res.data.today);
      if (tab === 'day' && !date) setDate(res.data.date);
      if (res.data.reps?.length) setReps(res.data.reps);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load scheduled calls');
    } finally {
      setLoading(false);
    }
  }, [tab, date, rep]);

  useEffect(() => { load(); }, [load]);

  const shiftDay = (n) => {
    if (!date) return;
    setDate(format(addDays(parseISO(date), n), 'yyyy-MM-dd'));
  };

  // Group by rep
  const groups = items.reduce((acc, i) => {
    (acc[i.rep_name] = acc[i.rep_name] || []).push(i);
    return acc;
  }, {});
  const pendingCount = items.filter(i => i.status === 'pending').length;
  const doneCount = items.length - pendingCount;

  const TabButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
        tab === id ? 'bg-gradient-to-r from-teal-700 to-emerald-500 text-white shadow-lg shadow-teal-500/25'
                   : 'bg-white text-slate-600 border border-slate-200 hover:bg-teal-50'}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  const CallRow = ({ i }) => (
    <div
      onClick={() => i.account_id && navigate(`/accounts/${i.account_id}`)}
      className="flex flex-wrap items-start justify-between gap-3 p-4 rounded-xl border border-slate-100 bg-white hover:bg-teal-50/40 cursor-pointer transition-colors"
    >
      <div className="min-w-[220px] flex-1">
        <p className="font-semibold text-slate-800">{i.doctor ? `Dr. ${i.doctor}` : 'Doctor not recorded'}</p>
        <p className="text-sm text-slate-500">{i.account_name}</p>
        {i.category && <p className="text-xs text-slate-400 mt-1">{i.category} · scheduled on {fmt(i.scheduled_on, 'dd MMM')}</p>}
      </div>
      <div className="flex-1 min-w-[220px]">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Follow-up action</p>
        {i.follow_up_actions?.length
          ? <ul className="text-sm text-slate-700 list-disc pl-4 space-y-0.5">{i.follow_up_actions.map((a, k) => <li key={k}>{a}</li>)}</ul>
          : <p className="text-sm text-slate-400">Not specified</p>}
      </div>
      <div className="text-right min-w-[140px]">
        {tab === 'overdue' ? (
          <>
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-semibold bg-red-100 text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" /> {i.days_overdue} day{i.days_overdue !== 1 ? 's' : ''} overdue
            </span>
            <p className="text-xs text-slate-400 mt-1">Was due {fmt(i.scheduled_date, 'dd MMM yyyy')}</p>
          </>
        ) : i.status === 'done' ? (
          <>
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-semibold bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Done
            </span>
            <p className="text-xs text-slate-400 mt-1">on {fmt(i.done_on, 'dd MMM')}{i.done_by ? ` by ${i.done_by}` : ''}</p>
          </>
        ) : (
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-semibold ${
            date < today ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
            <Clock className="h-3.5 w-3.5" /> {date < today ? 'Missed' : 'Pending'}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="scheduled-calls-page">
        <div className="mb-6">
          <h1 className="text-4xl font-bold tracking-tight text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <CalendarClock className="h-9 w-9 text-teal-700" /> Scheduled Calls
          </h1>
          <p className="text-base text-slate-500 mt-2">
            {isAdmin ? 'Follow-up calls scheduled by your team' : 'Follow-up calls you have scheduled'}. A call is marked done as soon as a new call is logged for that account.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <TabButton id="day" label="Calls by day" icon={CalendarClock} />
          <TabButton id="overdue" label="Overdue" icon={AlertTriangle} />

          {tab === 'day' && (
            <div className="flex items-center gap-1 ml-2">
              <button onClick={() => shiftDay(-1)} className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-teal-50" title="Previous day">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:border-teal-500 focus:outline-none" />
              <button onClick={() => shiftDay(1)} className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-teal-50" title="Next day">
                <ChevronRight className="h-4 w-4" />
              </button>
              {date !== today && (
                <button onClick={() => setDate(today)} className="ml-1 px-3 py-2 rounded-lg text-sm font-medium text-teal-700 hover:bg-teal-50">Today</button>
              )}
            </div>
          )}

          {isAdmin && (
            <select value={rep} onChange={e => setRep(e.target.value)}
              className="ml-auto px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:border-teal-500 focus:outline-none">
              <option value="">All reps</option>
              {reps.map(r => <option key={r.email} value={r.email}>{r.name}</option>)}
            </select>
          )}
        </div>

        {/* Summary */}
        {!loading && (
          <div className="flex flex-wrap gap-2 mb-4 text-sm">
            {tab === 'day' ? (
              <>
                <span className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700">
                  {fmt(date)} · {items.length} scheduled
                </span>
                <span className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 font-semibold">{doneCount} done</span>
                <span className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 font-semibold">{pendingCount} {date < today ? 'missed' : 'pending'}</span>
              </>
            ) : (
              <>
                <span className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 font-semibold">{items.length} overdue</span>
                {Object.entries(groups).map(([name, list]) => (
                  <span key={name} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600">
                    {name}: <b>{list.length}</b>
                  </span>
                ))}
              </>
            )}
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent"></div>
              <p className="text-slate-500 mt-4">Loading...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {tab === 'day' ? 'No calls scheduled for this day.' : 'No overdue calls. Everything is up to date! 🎉'}
            </div>
          ) : tab === 'overdue' ? (
            <div className="space-y-3">
              {items.map(i => (
                <div key={i.id}>
                  {isAdmin && <p className="text-xs text-slate-400 mb-1 flex items-center gap-1"><User className="h-3 w-3" /> {i.rep_name}</p>}
                  <CallRow i={i} />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groups).map(([name, list]) => (
                <div key={name}>
                  <h2 className="flex items-center gap-2 text-sm font-bold text-teal-800 uppercase tracking-wide mb-2">
                    <User className="h-4 w-4" /> {name}
                    <span className="text-slate-400 font-medium normal-case">({list.length} call{list.length !== 1 ? 's' : ''})</span>
                  </h2>
                  <div className="space-y-2">
                    {list.map(i => <CallRow key={i.id} i={i} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ScheduledCalls;
